import { dateLabel, lifeLabel } from "@/lib/dates";
import React from "react";
import { Link } from "react-router-dom";
import { Heart, Users } from "lucide-react";
import { PersonAvatar } from "@/components/PersonAvatar";
import { displayName } from "@/lib/names";
import { UNION_TYPES, UNION_STATUSES, LINK_TYPES } from "@/lib/relations";

const Mini = ({ p, testid, note }) => (
  <Link to={`/persons/${p.id}`} className="flex items-center gap-3 heritage-card p-3 hover:border-[#993D22]" data-testid={testid}>
    <PersonAvatar person={p} size={40} />
    <div className="min-w-0">
      <div className="font-serif-heritage text-lg text-[#261911] truncate">{displayName(p)}</div>
      <div className="text-xs text-[#6E5B4F]">{p.birth_year ? dateLabel(p, "birth") : "date inconnue"}{note ? ` · ${note}` : ""}</div>
    </div>
  </Link>
);

const linkNote = (c, parentId) => {
  const k = c.father_id === parentId ? c.father_link_type : c.mother_link_type;
  return k && k !== "biological" ? `lien ${LINK_TYPES[k]}` : "";
};

export const FamilyRelations = ({ person, allPersons }) => {
  const byId = Object.fromEntries((allPersons || []).map(p => [p.id, p]));
  const spouses = person.spouses || [];
  const children = person.children || [];
  const otherParent = c => (c.father_id === person.id ? c.mother_id : c.father_id);
  const groups = [...spouses.map(s => ({ key: s.id, partner: s, kids: children.filter(c => otherParent(c) === s.id) })),
    { key: "__other", partner: null, kids: children.filter(c => !spouses.some(s => s.id === otherParent(c))) }];
  const full = (allPersons || []).filter(p => p.id !== person.id && person.father_id && person.mother_id && p.father_id === person.father_id && p.mother_id === person.mother_id);
  const half = (allPersons || []).filter(p => p.id !== person.id && !full.includes(p) &&
    ((person.father_id && p.father_id === person.father_id) || (person.mother_id && p.mother_id === person.mother_id)));
  const halfNote = p => (p.father_id === person.father_id ? `même père${p.mother_id && byId[p.mother_id] ? `, mère : ${displayName(byId[p.mother_id])}` : ""}` : "même mère");

  return (
    <>
      <div className="mb-8" data-testid="unions-section">
        <div className="label-uppercase text-[#6E5B4F] mb-3 flex items-center gap-2"><Heart className="w-3.5 h-3.5" /> Unions et enfants</div>
        {groups.filter(g => g.partner || g.kids.length).length === 0 && <div className="text-sm italic text-[#6E5B4F]">— Aucune union ni enfant enregistré —</div>}
        <div className="space-y-4">
          {groups.filter(g => g.partner || g.kids.length).map((g, i) => {
            const u = g.partner ? (person.union_details || {})[g.partner.id] || {} : {};
            return (
              <div key={g.key} className="border-l-2 border-[#B8860B] pl-4" data-testid={`union-group-${g.key}`}>
                <div className="text-sm text-[#3A2312] mb-2">
                  {g.partner ? <>Union {i + 1} avec <Link to={`/persons/${g.partner.id}`} className="font-semibold text-[#993D22]">{displayName(g.partner)}</Link> · {UNION_TYPES[u.type || "unknown"]} · {UNION_STATUSES[u.status || "unknown"]}</> : "Enfants dont l'autre parent n'est pas renseigné"}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {g.kids.map(c => <Mini key={c.id} p={c} testid={`child-${c.id}`} note={linkNote(c, person.id)} />)}
                  {g.partner && !g.kids.length && <span className="text-xs italic text-[#6E5B4F]">Aucun enfant enregistré pour cette union</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {(full.length > 0 || half.length > 0) && (
        <div className="mb-8" data-testid="siblings-section">
          <div className="label-uppercase text-[#6E5B4F] mb-3 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Frères et sœurs</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {full.map(p => <Mini key={p.id} p={p} testid={`sibling-${p.id}`} note="mêmes parents" />)}
            {half.map(p => <Mini key={p.id} p={p} testid={`half-sibling-${p.id}`} note={`demi-frère/sœur (${halfNote(p)})`} />)}
          </div>
        </div>
      )}
    </>
  );
};

export default FamilyRelations;
